import {
  AppliedTaxRate as ApiAppliedTaxRate,
  Invoice as ApiInvoice,
  InvoiceCustomerAddress as ApiInvoiceCustomerAddress,
  InvoiceItem as ApiInvoiceItem,
  InvoiceStatus as ApiInvoiceStatus,
  TaxRate as ApiTaxRate,
  InvoiceStatus as InternalInvoiceStatus,
  ListInvoicesRequestFilters,
  ListInvoicesRequestFiltersDateFilterDateField,
} from '@vendasta/billing';
import { AppliedBundle, convertApiAppliedBundle, isAppliedBundle } from '../applied_bundle';
import { AppliedDiscount, appliedDiscountFromApi } from '../applied_discount';
import { convertAPINote, isNote } from '../note/note';
import { Currency, currencyFromApi } from '../pricing';
import {
  CollectionMethod,
  collectionMethodFromApi,
  PaymentMethodType,
  paymentTypesFromApi,
} from '../retail-customer-configuration';
import { TaxRate, isTaxRate } from '../tax-rate';
import { ListInvoicesFilter } from './list-invoice';

export enum InvoicePDFType {
  INVOICE = 'invoice',
  RECEIPT = 'receipt',
}

export enum InvoiceStatus {
  DRAFT = 'Draft',
  DUE = 'Due',
  PAID = 'Paid',
  VOID = 'Void',
}

export enum InvoiceDateField {
  CREATED = 'Created',
}

export enum InvoiceOrigin {
  MANUAL = 'Manual',
  SALES_ORDER = 'Sales Order',
  RENEWAL = 'Renewal',
  TEMPLATE = 'Template',
  UNSET = 'Unset',
}

export enum InvoiceOriginAPI {
  MANUAL = 'manual',
  SALES_ORDER = 'sales-order',
  RENEWAL = 'renewal',
  TEMPLATE = 'template',
}

const isInvoiceItem = (ii: InvoiceItem | undefined): ii is InvoiceItem => {
  return !!ii;
};

export interface InvoiceItem {
  id: string;
  sku: string;
  amount: number;
  quantity: number;
  description: string;
  subtotal: number;
  total: number;
  appliedTaxes?: AppliedTaxRate[];
  appliedDiscounts?: AppliedDiscount[];
  taxRates: TaxRate[];
  // DEPRECATED: a bundle is now represented as single item on the invoice rather than it being comprised of all individual products within said bundle.
  // New invoices will not be created with a bundle ID or bundle instance ID, however, we still need to support the fields to maintain the legacy bundle view so that we do not
  // break the historical representation of past invoices.
  bundleId: string;
  // DEPRECATED
  bundleInstanceId?: string;
}

export const isInvoice = (i: Invoice | undefined): i is Invoice => {
  return !!i;
};

export interface Invoice {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  contactId: string;
  status: InvoiceStatus;
  subtotal: number;
  discountTotal: number;
  total: number;
  paid?: Date;
  issued?: Date;
  due?: Date;
  created?: Date;
  updated?: Date;
  voided?: Date;
  autoAdvance?: Date;
  items: InvoiceItem[];
  currency: Currency;
  orderId: string;
  appliedTaxes: AppliedTaxRate[];
  defaultTaxRates: TaxRate[];
  // DEPRECATED: a bundle is now represented as single item on the invoice rather than it being comprised of all individual products within said bundle.
  // New invoices will not have applied bundles, however, we still need to support the field to maintain the legacy bundle view so that we do not
  // break the historical representation of past invoices.
  appliedBundles: AppliedBundle[];
  origin: InvoiceOrigin;
  collectionMethod: CollectionMethod;
  memo: string;
  defaultPaymentMethod: string;
  customerAddress: InvoiceCustomerAddress;
  externalCustomerIdentifier: string;
  notes: Note[];
  additionalContactIds?: string[];
  paymentMethodTypes: PaymentMethodType[];
}

export interface Note {
  message: string;
}

const isAppliedTaxRate = (tr: AppliedTaxRate | undefined): tr is AppliedTaxRate => {
  return !!tr;
};

export interface AppliedTaxRate {
  amount?: number;
  taxRateId?: string;
  percentage?: number;
  name?: string;
  description?: string;
}

function convertApiStatus(s: ApiInvoiceStatus): InvoiceStatus {
  switch (s) {
    case ApiInvoiceStatus.DUE:
      return InvoiceStatus.DUE;
    case ApiInvoiceStatus.PAID:
      return InvoiceStatus.PAID;
    case ApiInvoiceStatus.VOID:
      return InvoiceStatus.VOID;
    default:
      return InvoiceStatus.DRAFT;
  }
}

function invoiceStatusToApi(status: InvoiceStatus): InternalInvoiceStatus {
  switch (status) {
    case InvoiceStatus.DUE:
      return InternalInvoiceStatus.DUE;
    case InvoiceStatus.PAID:
      return InternalInvoiceStatus.PAID;
    case InvoiceStatus.VOID:
      return InternalInvoiceStatus.VOID;
    default:
      return InternalInvoiceStatus.DRAFT;
  }
}

function invoiceDateFilterFieldToApi(dateField: InvoiceDateField): ListInvoicesRequestFiltersDateFilterDateField {
  switch (dateField) {
    default:
      return ListInvoicesRequestFiltersDateFilterDateField.CREATED;
  }
}

function convertApiOrigin(s: string): InvoiceOrigin {
  switch (s) {
    case InvoiceOriginAPI.MANUAL:
      return InvoiceOrigin.MANUAL;
    case InvoiceOriginAPI.SALES_ORDER:
      return InvoiceOrigin.SALES_ORDER;
    case InvoiceOriginAPI.RENEWAL:
      return InvoiceOrigin.RENEWAL;
    case InvoiceOriginAPI.TEMPLATE:
      return InvoiceOrigin.TEMPLATE;
    default:
      return InvoiceOrigin.UNSET;
  }
}

export function convertApiAppliedTaxRate(at: ApiAppliedTaxRate): AppliedTaxRate | undefined {
  if (!at) {
    return undefined;
  }
  return {
    amount: at.amount || 0,
    taxRateId: at.taxRateId || '',
    percentage: at.percentage || 0,
    name: at.name || '',
    description: at.description || '',
  };
}

export function convertAppliedTaxRate(at: AppliedTaxRate): ApiAppliedTaxRate | undefined {
  if (!at) {
    return undefined;
  }
  return new ApiAppliedTaxRate({
    amount: at.amount || 0,
    taxRateId: at.taxRateId || '',
  });
}

export function convertApiTaxRate(tr: ApiTaxRate): TaxRate | undefined {
  if (!tr) {
    return undefined;
  }
  return {
    id: tr.id || '',
    name: tr.name || '',
    description: tr.description || '',
    percentage: tr.percentage || 0,
  };
}

export function convertTaxRate(tr: TaxRate): ApiTaxRate | undefined {
  if (!tr) {
    return undefined;
  }
  return new ApiTaxRate({
    id: tr.id,
    name: tr.name,
    description: tr.description,
    percentage: tr.percentage,
  });
}

export interface InvoiceCustomerAddress {
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

function convertApiCustomerAddress(ca: ApiInvoiceCustomerAddress): InvoiceCustomerAddress {
  return {
    address: ca?.address || '',
    address2: ca?.address2 || '',
    city: ca?.city || '',
    state: ca?.state || '',
    zip: ca?.zip || '',
    country: ca?.country || '',
  };
}

export function convertApiInvoiceItem(ii: ApiInvoiceItem): InvoiceItem | undefined {
  if (!ii) {
    return undefined;
  }

  const appliedTaxes: AppliedTaxRate[] = (ii.appliedTaxes || [])
    .map((at) => convertApiAppliedTaxRate(at))
    .filter(isAppliedTaxRate);
  const taxRates: TaxRate[] = (ii.taxRates || []).map((tr) => convertApiTaxRate(tr)).filter(isTaxRate);
  const appliedDiscounts: AppliedDiscount[] = (ii.discounts || []).map((ad) => appliedDiscountFromApi(ad));

  return {
    id: ii.id || '',
    sku: ii.sku || '',
    amount: ii.amount || 0,
    quantity: ii.quantity || 0,
    description: ii.description || '',
    subtotal: ii.subtotal || 0,
    total: ii.total || 0,
    appliedDiscounts: appliedDiscounts || [],
    appliedTaxes: appliedTaxes || [],
    taxRates: taxRates || [],
    bundleId: ii.bundleId || '',
    bundleInstanceId: ii.bundleInstanceId || '',
  };
}

export function convertInvoiceItem(ii: InvoiceItem): ApiInvoiceItem | undefined {
  if (!ii) {
    return undefined;
  }

  return new ApiInvoiceItem({
    sku: ii.sku,
    amount: ii.amount,
    quantity: ii.quantity,
    description: ii.description,
    total: ii.total,
  });
}

export function convertApiInvoice(i: ApiInvoice): Invoice {
  const zeroDate = new Date('0001-01-01T00:00:00Z').valueOf();
  const invoiceItems: InvoiceItem[] = (i.items || []).map((ii) => convertApiInvoiceItem(ii)).filter(isInvoiceItem);
  const appliedTaxes: AppliedTaxRate[] = (i.appliedTaxes || [])
    .map((at) => convertApiAppliedTaxRate(at))
    .filter(isAppliedTaxRate);
  const taxRates: TaxRate[] = (i.defaultTaxRates || []).map((tr) => convertApiTaxRate(tr)).filter(isTaxRate);
  const appliedBundles: AppliedBundle[] = (i.appliedBundles || [])
    .map((at) => convertApiAppliedBundle(at))
    .filter(isAppliedBundle);
  const notes: Note[] = (i.notes || []).map((n) => convertAPINote(n)).filter(isNote);

  return {
    id: i.id || '',
    number: i.number || '',
    customerId: i.customerId || '',
    customerName: i.customerName || '',
    contactId: i.contactId || '',
    status: convertApiStatus(i.status),
    subtotal: i.subtotal || 0,
    total: i.total || 0,
    discountTotal: i.amountDiscount || 0,
    paid: i.paid && i.paid.valueOf() !== zeroDate ? i.paid : undefined,
    issued: i.issued && i.issued.valueOf() !== zeroDate ? i.issued : undefined,
    due: i.due && i.due.valueOf() !== zeroDate ? i.due : undefined,
    voided: i.voided && i.voided.valueOf() !== zeroDate ? i.voided : undefined,
    created: i.created.valueOf() !== zeroDate ? i.created : undefined,
    updated: i.updated.valueOf() !== zeroDate ? i.updated : undefined,
    autoAdvance: i.autoAdvance.valueOf() !== zeroDate ? i.autoAdvance : undefined,
    items: invoiceItems || [],
    currency: currencyFromApi(i.currency),
    orderId: i.orderId || '',
    appliedTaxes: appliedTaxes || [],
    defaultTaxRates: taxRates || [],
    appliedBundles: appliedBundles || [],
    origin: convertApiOrigin(i.origin),
    collectionMethod: collectionMethodFromApi(i.collectionMethod),
    memo: i.memo || '',
    defaultPaymentMethod: i.defaultPaymentMethod || '',
    customerAddress: convertApiCustomerAddress(i.customerAddress),
    externalCustomerIdentifier: i.externalCustomerIdentifier || '',
    notes: notes || [],
    additionalContactIds: i.additionalContactIds || [],
    paymentMethodTypes: paymentTypesFromApi(i.paymentMethodTypes),
  };
}

export function listInvoiceFiltersToApi(filters: ListInvoicesFilter): ListInvoicesRequestFilters {
  return new ListInvoicesRequestFilters({
    ...filters,
    statuses: (filters.statuses || []).map((s) => invoiceStatusToApi(s)),
    origins: filters.origins,
    collectionMethods: filters.collectionMethods,
    dateFilter: {
      dateGte: filters.dateFilter?.dateGte,
      dateLte: filters.dateFilter?.dateLte,
      dateField: filters.dateFilter?.dateField ? invoiceDateFilterFieldToApi(filters.dateFilter.dateField) : undefined,
    },
  });
}
